import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FlowModal from "../components/FlowModal.jsx";
import ResultModal from "../components/ResultModal.jsx";
import Shimmer from "../components/Shimmer.jsx";
import HowLoansWork from "../components/HowLoansWork.jsx";
import { StatCard, STAT_ICONS } from "../components/StatRow.jsx";
import config from "../config/index.js";
import { useWallet } from "../context/WalletContext.jsx";
import { useDeposits } from "../hooks/useDeposits.js";
import { getContract } from "../services/contracts.js";
import { getTxOverrides } from "../services/gas.js";
import { fetchBorrowPreview } from "../services/api.js";
import { decodeContractError } from "../utils/contractError.js";
import { parseUsdc, formatUsdcCompact, depositStateLabel, formatLongDate } from "../utils/format.js";

const TOKEN_SYMBOL = "USDC";

/**
 * Borrow USDC against an active deposit's Safe Vault principal. Borrow
 * capacity is computed client-side as `principal × safeVaultBps × maxLtvBps`
 * for visibility; the contract enforces the actual cap. Visually mirrors
 * the Deposit / My TD's pages — mint cards on a light-mint page, dark
 * forest-green CTA, gold accents on selected items / active info.
 */
export default function Lending() {
    const { address, signer } = useWallet();
    const { deposits, isLoading, refresh } = useDeposits(address);
    const [selectedId, setSelectedId] = useState(null);
    const [amount, setAmount] = useState("");
    const [busy, setBusy] = useState(false);

    // §M6 — the desktop body-scroll lock is GONE. It existed so the page sat
    // flush at viewport height with the deposit list as the only scroller,
    // but the client asked for the "How this loan works" bar at the bottom of
    // this page; with the lock on, anything past the fold was unreachable.
    // The deposit list keeps its own inner scroller either way.

    const [flowOpen, setFlowOpen] = useState(false);
    const [steps, setSteps] = useState(buildInitialSteps(""));
    const [result, setResult] = useState(null);

    function setStepStatus(key, status) {
        setSteps((prev) => prev.map((s) => (s.key === key ? { ...s, status } : s)));
    }

    // §V6.2 — Lending only operates on Standard deposits. Turbo
    // deposits revert any user-driven loan
    // (`NoBorrowingAgainstTurbo`) — the product already deploys the
    // Safe Vault slice as leverage at creation. Filter by the
    // IMMUTABLE `productMode === 1`, not the mutable `compoundMode`
    // (which the user can flip on/off without changing the product).
    // §M6 (Jul 2026 client review) — "Could this be date order with the most
    // recent TD at the top?" The list came off the API in ascending depositId
    // order, so the deposit the user had just opened was buried at the bottom
    // of the scroller. Newest first, by chain start time.
    const active = deposits
        .filter((d) => depositStateLabel(d.state) === "Active" && Number(d.productMode) !== 1)
        .sort((a, b) => Number(b.startTime) - Number(a.startTime));
    const selected = active.find((d) => d.id === selectedId) || active[0];

    /** Per-TD available-to-borrow = (principal × 80% × 75%) − outstanding loan. */
    function availableToBorrow(d) {
        const cap = (BigInt(d.principal) *
            BigInt(config.protocol.safeVaultBps) *
            BigInt(config.protocol.maxLtvBps)) /
            (10000n * 10000n);
        const loan = BigInt(d.loanBalance);
        return cap > loan ? cap - loan : 0n;
    }

    // Borrow capacity comes entirely from THIS deposit's own SafeVault
    // slice (per spec §6 self-funded loans). No protocol-wide pool cap
    // is needed because SafeVault enforces per-deposit isolation:
    //   capacity = principal × 80% (safeVault) × 75% (maxLTV) − outstanding
    const borrowCapacity = selected
        ? (BigInt(selected.principal) *
              BigInt(config.protocol.safeVaultBps) *
              BigInt(config.protocol.maxLtvBps)) /
              (10000n * 10000n) -
          BigInt(selected.loanBalance)
        : 0n;
    const borrowCapacityUsdc = Number(borrowCapacity) / 10 ** config.protocol.usdcDecimals;
    const numericAmount = Number(amount);
    const amountIsValid =
        Number.isFinite(numericAmount) && numericAmount > 0 && numericAmount <= borrowCapacityUsdc;
    const amountExceedsCapacity =
        Number.isFinite(numericAmount) && numericAmount > 0 && numericAmount > borrowCapacityUsdc;

    function handleAmountChange(event) {
        const next = event.target.value;
        if (next === "") {
            setAmount("");
            return;
        }
        const parsed = Number(next);
        if (!Number.isFinite(parsed) || parsed < 0) return;
        if (parsed > borrowCapacityUsdc) {
            setAmount(String(borrowCapacityUsdc));
            return;
        }
        setAmount(next);
    }

    // Resulting-LTV preview (§F.3 / §H.2). Debounced backend call —
    // the projection mirrors the contract's _collateralValue / LTV math
    // so the user sees what their position would look like AFTER the
    // borrow lands, not just the current capacity.
    const [borrowPreview, setBorrowPreview] = useState(null);
    useEffect(() => {
        if (!selected || !amountIsValid) {
            setBorrowPreview(null);
            return undefined;
        }
        const drawUsdc = parseUsdc(amount).toString();
        const handle = setTimeout(async () => {
            try {
                const data = await fetchBorrowPreview(selected.id, drawUsdc);
                setBorrowPreview(data);
            } catch {
                setBorrowPreview(null);
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [selected?.id, amount, amountIsValid]);

    async function handleBorrow() {
        if (!signer || !selected || !amountIsValid) return;
        setBusy(true);
        setSteps(buildInitialSteps(amount));
        setFlowOpen(true);
        setResult(null);

        try {
            const lending = getContract("lending", signer);
            const overrides = await getTxOverrides(signer.provider);
            setStepStatus("borrow", "loading");
            const tx = await lending.openLoan(selected.id, parseUsdc(amount), overrides);
            await tx.wait();
            setStepStatus("borrow", "done");

            await delay(300);
            setFlowOpen(false);
            setResult({
                type: "success",
                title: "Loan Opened",
                message: `Borrowed ${amount} ${TOKEN_SYMBOL} against Term Deposit #${selected.id}. The amount has been transferred to your wallet.`
            });
            setAmount("");
            await refresh();
        } catch (err) {
            setSteps((prev) =>
                prev.map((s) => (s.status === "loading" ? { ...s, status: "error" } : s))
            );
            await delay(300);
            setFlowOpen(false);
            setResult({
                type: "error",
                title: "Borrow Failed",
                message: decodeContractError(err)
            });
        } finally {
            setBusy(false);
        }
    }

    if (!address) {
        return (
            <div className="-mt-6 flex min-h-[60vh] items-center justify-center">
                <div className="rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] px-8 py-10 text-center max-w-md">
                    <h2 className="font-manrope text-[18px] font-bold text-[#e6c985]">Wallet Required</h2>
                    <p className="mt-2 text-[12px] text-[#e6c985]/80">
                        Connect your wallet to use the lending module.
                    </p>
                </div>
            </div>
        );
    }

    const stats = [
        {
            label: "Active Deposits",
            value: String(active.length),
            unit: "",
            delta: active.length > 0 ? "Eligible" : "None",
            iconSrc: STAT_ICONS.active
        },
        {
            label: "Selected Principal",
            value: selected ? `$${formatUsdcCompact(selected.principal)}` : "$0.00",
            unit: TOKEN_SYMBOL,
            delta: selected ? `Term Deposit #${selected.id}` : "—",
            iconSrc: STAT_ICONS.holdings
        },
        {
            label: "Borrow Capacity",
            value: `$${formatUsdcCompact(borrowCapacity)}`,
            unit: TOKEN_SYMBOL,
            delta: `${config.protocol.maxLtvBps / 100}% LTV cap`,
            iconSrc: STAT_ICONS.safe
        }
    ];

    return (
        <div className="flex flex-col gap-3 lg:min-h-[calc(100vh-9rem)]">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-end gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#1D3E29] px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#FBC206]">
                    <CoinIcon />
                    Standard Borrow
                </span>
            </div>

            {/* Stat strip */}
            <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {stats.map((item) => (
                    <StatCard key={item.label} {...item} loading={isLoading} />
                ))}
            </div>

            {/* Terms strip */}
            <div className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] px-4 py-2.5">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <h3 className="shrink-0 font-manrope text-[12px] font-bold uppercase tracking-widest text-[#e6c985] sm:pr-4 sm:border-r sm:border-white/15">
                        Loan Terms
                    </h3>
                    <ul className="grid flex-1 grid-cols-2 gap-x-4 gap-y-1.5 text-[10.5px] leading-snug text-white sm:grid-cols-4 sm:pl-4">
                        <Bullet>Max LTV {config.protocol.maxLtvBps / 100}%</Bullet>
                        <Bullet>Liquidation at {config.protocol.liquidationThresholdBps / 100}%</Bullet>
                        <Bullet>Loan APR {config.protocol.loanAprBps / 100}%</Bullet>
                        <Bullet>Repay anytime</Bullet>
                    </ul>
                </div>
            </div>

            {/* Main row — picker + borrow form */}
            <div className="flex flex-col gap-3 lg:flex-row lg:flex-1 lg:min-h-0">
                <section className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 lg:flex lg:w-[58.333%] lg:flex-col lg:overflow-hidden">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">Pick a Deposit</h3>
                        <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                            {active.length} eligible
                        </span>
                    </div>

                    <ul
                        className="mt-3 max-h-[360px] space-y-2 overflow-y-auto overscroll-contain p-1 lg:max-h-none lg:flex-1 lg:min-h-0"
                        style={{ scrollSnapType: "y proximity" }}
                    >
                        {isLoading && active.length === 0
                            ? Array.from({ length: 3 }).map((_, i) => (
                                  <li
                                      key={`skel-${i}`}
                                      className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-2.5"
                                  >
                                      <div className="flex items-center justify-between">
                                          <Shimmer width="6rem" height="0.875rem" />
                                          <Shimmer width="3rem" height="0.875rem" />
                                      </div>
                                  </li>
                              ))
                            : null}
                        {!isLoading && active.length === 0 ? (
                            <li className="rounded-[10px] bg-white/10 ring-1 ring-[#bfb67b] px-3 py-3 text-center text-[11px] text-[#e6c985]/70">
                                No active deposits available. Open a Term Deposit first to borrow against it.
                            </li>
                        ) : null}
                        {active.map((d) => {
                            const isSelected = (selected?.id || null) === d.id;
                            const available = availableToBorrow(d);
                            return (
                                <li key={d.id} style={{ scrollSnapAlign: "start", scrollSnapStop: "always" }}>
                                    <button
                                        type="button"
                                        onClick={() => setSelectedId(d.id)}
                                        className={`flex w-full items-center justify-between rounded-[10px] px-3 py-2.5 text-left ring-1 transition-colors ${
                                            isSelected
                                                ? "bg-[#FBC206] text-[#10281b] ring-[#FBC206]"
                                                : "bg-white/10 text-white ring-[#bfb67b] hover:bg-white/20"
                                        }`}
                                    >
                                        <div>
                                            <div className="font-manrope text-[12.5px] font-bold">
                                                Term Deposit #{String(d.id).padStart(6, "0")}
                                            </div>
                                            <div
                                                className={`mt-0.5 text-[10px] ${
                                                    isSelected ? "text-[#10281b]/80" : "text-[#e6c985]/70"
                                                }`}
                                            >
                                                Principal ${formatUsdcCompact(d.principal)} · Loan ${formatUsdcCompact(d.loanBalance)}
                                            </div>
                                            {/* §M6 — "Are we able to put the date this
                                                was initiated in the field? To avoid
                                                International date convention confusion
                                                put '26 July 2026' rather than 26/7/26."
                                                `formatLongDate` spells the month out for
                                                exactly that reason. */}
                                            <div
                                                className={`mt-0.5 text-[10px] ${
                                                    isSelected ? "text-[#10281b]/70" : "text-[#e6c985]/55"
                                                }`}
                                            >
                                                Opened {formatLongDate(d.startTime)}
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <div className="font-manrope text-[13px] font-bold">
                                                ${formatUsdcCompact(available)}
                                            </div>
                                            <div
                                                className={`text-[10px] ${
                                                    isSelected ? "text-[#10281b]/70" : "text-[#e6c985]/60"
                                                }`}
                                            >
                                                Available to borrow
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </section>

                {/* Borrow card */}
                <section className="rounded-[12px] bg-[#10281b] ring-1 ring-[#bfb67b] p-4 lg:w-[41.666%] lg:self-start">
                    <div className="flex items-center justify-between">
                        <h3 className="font-manrope text-[12px] font-bold text-[#e6c985]">
                            Borrow {TOKEN_SYMBOL}
                        </h3>
                        <span className="text-[9px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                            APR {config.protocol.loanAprBps / 100}%
                        </span>
                    </div>

                    <div className="mt-3 flex items-center gap-2 rounded-[10px] bg-[#162e1f] ring-1 ring-[#bfb67b] px-3 py-1.5">
                        <span className="font-manrope text-[18px] font-bold text-[#e6c985]/45">$</span>
                        <input
                            type="number"
                            min={0}
                            max={borrowCapacityUsdc}
                            step="0.01"
                            value={amount}
                            onChange={handleAmountChange}
                            placeholder="0"
                            disabled={!selected || borrowCapacityUsdc === 0}
                            className="flex-1 min-w-0 bg-transparent font-manrope text-[20px] font-bold text-white placeholder:text-white/30 focus:outline-none disabled:cursor-not-allowed disabled:opacity-60 [color-scheme:dark]"
                        />
                        <span className="rounded-md bg-[#FBC206] px-2 py-0.5 text-[10px] font-semibold text-[#10281b]">
                            {TOKEN_SYMBOL}
                        </span>
                    </div>

                    <div className="mt-2 flex items-center justify-between text-[10px] text-[#e6c985]/70">
                        <span>
                            Max:{" "}
                            <span className="font-semibold text-white">
                                ${formatUsdcCompact(borrowCapacity)}
                            </span>
                        </span>
                        <button
                            type="button"
                            disabled={!selected || borrowCapacityUsdc === 0}
                            onClick={() => setAmount(String(borrowCapacityUsdc))}
                            className="rounded-md bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Max
                        </button>
                    </div>

                    {amountExceedsCapacity ? (
                        <p className="mt-2 text-[10px] font-medium text-[#C93030]">
                            Amount exceeds borrow capacity — clamped to maximum.
                        </p>
                    ) : null}

                    {borrowPreview ? (
                        <div className="mt-3 grid grid-cols-2 gap-2 rounded-[8px] bg-white/5 ring-1 ring-[#bfb67b]/40 px-2.5 py-2 text-[10.5px] text-white">
                            <div>
                                <div className="text-[9px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                                    Resulting LTV
                                </div>
                                <div className="mt-0.5 font-manrope text-[14px] font-bold text-white">
                                    {(borrowPreview.projectedLtvBps / 100).toFixed(1)}%
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-[9px] font-semibold uppercase tracking-widest text-[#e6c985]/70">
                                    Headroom to Max
                                </div>
                                <div className="mt-0.5 font-manrope text-[14px] font-bold text-white">
                                    {(borrowPreview.headroomBps / 100).toFixed(1)}%
                                </div>
                            </div>
                            <div className="col-span-2 h-1.5 overflow-hidden rounded-full bg-white/10">
                                <div
                                    className="h-full rounded-full"
                                    style={{
                                        // Fill relative to the MAX-LTV cap (not a 0–100 LTV
                                        // scale) so the bar reads as "progress to max": at the
                                        // borrow cap, headroom is 0% and the bar is 100% full.
                                        width: `${Math.min(100, (borrowPreview.projectedLtvBps / borrowPreview.maxLtvBps) * 100)}%`,
                                        backgroundColor:
                                            borrowPreview.projectedLtvBps >= borrowPreview.liquidationThresholdBps
                                                ? "#C93030"
                                                : borrowPreview.projectedLtvBps >= borrowPreview.maxLtvBps
                                                ? "#FBC206"
                                                : "#22C55E"
                                    }}
                                />
                            </div>
                        </div>
                    ) : null}

                    <div className="mt-3 rounded-[8px] bg-white/10 ring-1 ring-[#bfb67b] px-2.5 py-2 text-[10.5px] leading-snug text-white">
                        <div className="font-semibold">Notice</div>
                        <p className="mt-0.5 text-[#e6c985]/75">
                            Liquidation triggers at {config.protocol.liquidationThresholdBps / 100}%
                            LTV. This can only happen after your term has finished, so
                            remember to exit your position at the end of the 3 year term
                            or you could risk losing it.
                        </p>
                    </div>

                    <button
                        type="button"
                        disabled={busy || !selected || !amountIsValid}
                        onClick={handleBorrow}
                        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#FBC206] px-4 py-2.5 font-manrope text-[13px] font-semibold text-[#10281b] hover:-translate-y-0.5 hover:shadow-md transition-all disabled:cursor-not-allowed disabled:opacity-50 disabled:translate-y-0 disabled:hover:shadow-none"
                    >
                        {busy ? "Submitting..." : "Borrow Now"}
                        <ChevronRight />
                    </button>
                </section>
            </div>

            {/* §M6 (Jul 2026 client review) — "I think we need more
                information about the loans on this page. Perhaps add this bar
                at the bottom (it is taken from /loans/50)." Same component the
                loan detail page renders, so the copy can never fork. */}
            <HowLoansWork className="mt-3" />

            <FlowModal
                open={flowOpen}
                title="Opening Loan"
                subtitle="Confirm the borrow transaction in your wallet."
                steps={steps}
            />

            <ResultModal
                open={result !== null}
                type={result?.type}
                title={result?.title || ""}
                message={result?.message || ""}
                primaryAction={
                    result?.type === "success" ? { label: "Go to Loans", to: "/loans" } : undefined
                }
                onClose={() => setResult(null)}
            />
        </div>
    );
}

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Locks document scroll on desktop (≥1024px) while mounted so the page
 * stays exactly flush at viewport height — no stray 1–2px body scroll
 * from header/padding rounding. Mobile keeps natural scroll since the
 * stat strip + main row stack vertically.
 */
function buildInitialSteps(amount) {
    const display = amount ? `${amount} ${TOKEN_SYMBOL}` : TOKEN_SYMBOL;
    return [
        {
            key: "borrow",
            label: `Borrowing ${display}`,
            doneLabel: `Borrowed ${display}`,
            status: "pending"
        }
    ];
}

function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

function Bullet({ children }) {
    return (
        <li className="flex items-start gap-1.5">
            <span className="mt-0.5 inline-flex h-3 w-3 shrink-0 items-center justify-center rounded-full bg-[#FBC206] text-[7px] font-bold text-[#10281b]">
                ✓
            </span>
            <span>{children}</span>
        </li>
    );
}

function ChevronLeft() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="13 4 7 10 13 16" />
        </svg>
    );
}

function ChevronRight() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}

function CoinIcon() {
    return (
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <ellipse cx="10" cy="5" rx="6" ry="2" />
            <path d="M4 5v4c0 1.1 2.7 2 6 2s6-0.9 6-2V5" />
            <path d="M4 9v4c0 1.1 2.7 2 6 2s6-0.9 6-2V9" />
        </svg>
    );
}
